import {autoPlacement, flip, MiddlewareData, Placement, VirtualElement} from '@floating-ui/react-dom';
import {CSSProperties, RefObject} from 'react';

import {useMouseCursorContext} from '../mouseCursor/mouseCursorContext';
import {useRepositionBoundary} from './useRepositionBoundary';

/*
 * Types.
 */

export type BoundsRect = DOMRectReadOnly;
export type BoundsRectProvider = () => BoundsRect;
export type Anchor = RefObject<HTMLElement> | BoundsRectProvider | VirtualElement;
export type ReferenceObject = VirtualElement | Element;
export interface RepositionArrowProps {
  style?: CSSProperties;
  ref?: RefObject<HTMLDivElement>;
}
export type RepositionPlacement = Placement | 'cursor' | 'auto';

/*
 * API.
 */

export function buildReferenceObject(anchor: Anchor): ReferenceObject {
  // If this is a rectangle, build the reference object.
  if (typeof anchor === 'function') {
    return {
      getBoundingClientRect: anchor,
    };
  }

  // Anchor is a VirtualElement
  if (!('current' in anchor)) {
    return anchor;
  }

  const currentAnchor = anchor.current;
  if (!currentAnchor) {
    throw new Error('The provided anchor Ref does not have a value.');
  }

  return currentAnchor;
}

const placementAlignmentMap = {
  auto: undefined,
  cursor: undefined,
  'auto-start': 'start',
  'auto-end': 'end',
} as const;

export function useParsePlacement(
  placement: RepositionPlacement | undefined,
  ignoreRepositionBoundary: boolean | undefined,
) {
  const boundary = useRepositionBoundary(ignoreRepositionBoundary);

  const shouldTrackCursor = placement === 'cursor';
  const cursorPosition = useMouseCursorContext(shouldTrackCursor);

  switch (placement) {
    case 'auto':
    case 'cursor':
      return {
        flipMiddleware: autoPlacement({
          alignment: placementAlignmentMap[placement],
          boundary,
        }),
        boundary,
        cursorPosition: shouldTrackCursor ? cursorPosition : undefined,
      };
    default:
      return {
        flipMiddleware: flip({boundary}),
        parsedPlacement: placement,
        boundary,
      };
  }
}

export function buildRepositionArrowStyles(
  arrowRef: RefObject<HTMLDivElement>,
  middlewareData: MiddlewareData,
) {
  return {
    ref: arrowRef,
    style: {
      position: 'absolute',
      top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : undefined,
      left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : undefined,
    } satisfies CSSProperties,
  };
}
